import { useLocale } from "@jmc/core/src/hooks/useLocale/index";
import urlHelper from "@jmc/solid-design-system/src/utils/url-helper";
import { locale2lang } from "@jmc/utils/utils/locale2lang";
import { useLocation } from "@reach/router";
import { ApplicationState } from "@redux/modules";
import { AccessLevel, PageContext, SeoItemList } from "@types";
import isEqual from "lodash/isEqual";
import React from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import yn from "yn";

import getPageTags from "../../utils/getPageTags";

interface OpenGraph {
    title?: string;
    description?: string;
    image?: { url?: string };
}

interface PropTypes {
    title: string;
    description?: string;
    canonical?: string;
    hreflangs?: string[];
    clonesHreflangs?: { url: string; locale: string }[];
    availableLocale?: {
        locale?: string[];
        url?: string;
    };
    level?: AccessLevel;
    index?: "index" | "noindex" | "default" | null;
    context?: PageContext;
    service?: boolean;
    paywall?: boolean;
    currentPageUrl?: string;
    twitter?: OpenGraph;
    open_graph?: OpenGraph;
    uid?: string;
    contentType?: string;
    maintenancePage?: boolean;
    noIndexImages?: boolean;
    faqs?: SeoItemList;
}

export const SEO: React.FunctionComponent<PropTypes> = ({
    title,
    description,
    canonical,
    hreflangs,
    clonesHreflangs,
    availableLocale,
    level = AccessLevel.level1,
    index = "default",
    context,
    service = false,
    paywall = false,
    currentPageUrl,
    twitter,
    open_graph,
    uid = null,
    contentType = null,
    maintenancePage = false,
    noIndexImages = false,
    faqs,
}: PropTypes): JSX.Element => {
    const location = useLocation();
    const canonicalUrl = canonical ? canonical : location?.href?.split("?")?.[0];
    const eventState = useSelector((state) => (state as ApplicationState).event, isEqual);
    const locale = useLocale();
    const pageTags = getPageTags(context, eventState);
    const { t } = useTranslation();
    const hasPaywall = paywall && yn(process.env.GATSBY_PAYWALL_ENABLED);
    const hasPaywallL1 =
        paywall && yn(process.env.GATSBY_SHOW_PAYWALL_JSONLD_ON_LVL1_PAGES) && level === AccessLevel.level1;
    const url = typeof window !== "undefined" ? window.location.href : "";
    const paywalledldJson = {
        "@context": "http://schema.org",
        "@type": "MedicalWebPage",
        mainEntityOfPage: {
            "@type": "WebPage",
            "@id": urlHelper.getFullUrl(currentPageUrl, locale, false),
        },
        isAccessibleForFree: "False",
        hasPart: { "@type": "WebPageElement", isAccessibleForFree: "False", cssSelector: ".paywall" },
    };

    const publicldJson = {
        "@context": "https://schema.org",
        "@type": "WebSite",
        author: {
            "@type": "Organization",
            "@id": "https://www.janssen.com/",
            name: "Janssen Pharmaceuticals",
            legalName: t("Janssen Pharmaceutica NV", { ns: "common" }),
            sameAs: "https://www.janssen.com, https://www.janssenmedicalcloud.com, https://www.janssenwithme.com, https://en.wikipedia.org/wiki/Janssen_Pharmaceuticals, https://twitter.com/JanssenGlobal, https://www.linkedin.com/company/janssen-pharmaceutical-companies-of-johnson-and-johnson",
            image: "https://www.janssen.com/sites/all/themes/janssen/logo.png",
        },
    };

    const shouldShowNoIndexTag = (): boolean => {
        if (maintenancePage) return true;
        if (index === "index") return false;
        if (index === "default" && level === AccessLevel.level1) return false;
        if (hasPaywall && level !== AccessLevel.level1) return false;
        if (
            level === AccessLevel.level2 &&
            index === "default" &&
            !hasPaywall &&
            yn(process.env.GATSBY_INDEX_LEVEL2_PAGES)
        )
            return false;

        return true;
    };

    return (
        <Helmet
            bodyAttributes={{
                ...(uid ? { "data-pageref": uid } : {}),
                ...(contentType ? { "data-contenttype": contentType } : {}),
                "data-locale": locale,
            }}
            htmlAttributes={{
                lang: locale2lang(locale),
            }}
        >
            <meta charSet="utf-8" />
            {description && <meta name="description" content={description} />}
            <meta name="purpose" content={pageTags.purpose} />
            {service && <meta name="service" content={pageTags.service} />}
            {!service && <meta name="product" content={pageTags.product} />}
            {!service && <meta name="emea_product" content={pageTags.emea_product} />}
            {!service && <meta name="specialty" content={pageTags.specialty} />}
            {!service && <meta name="emea_specialty" content={pageTags.emea_specialty} />}
            <meta
                name="content_topic"
                content={pageTags.contenttopic?.value}
                data-source={pageTags.contenttopic?.source}
            />
            <meta
                name="key_message_group"
                content={pageTags.key_message_group?.value}
                data-source={pageTags.key_message_group?.source}
            />
            <meta
                name="key_message_sub_group"
                content={pageTags.key_message_sub_group?.value}
                data-source={pageTags.key_message_sub_group?.source}
            />
            <meta name="type_of_interaction" content={pageTags.type_of_interaction} />
            <meta name="auto_tag_content" content={pageTags.auto_tag_content} />
            {contentType && <meta name="content_type" content={contentType} />}
            {paywall && level !== AccessLevel.level1 && <meta name="robots" content="noarchive" />}
            {noIndexImages && (
                <meta name="robots" content="noimageindex, max-image-preview:none, max-video-preview:0" />
            )}
            <link rel="canonical" href={canonicalUrl} />
            {hreflangs?.length > 0 &&
                hreflangs.map((path) => {
                    try {
                        const url = new URL(path);
                        const linkLocale = url.pathname.split("/")[1];
                        try {
                            const verifyLocale = new Intl.Locale(linkLocale);
                            if (verifyLocale) {
                                return <link rel="alternate" hrefLang={linkLocale} href={path} key={linkLocale} />;
                            }
                        } catch (e) {
                            // eslint-disable-next-line no-console
                            console.error(e);
                        }
                    } catch (e) {
                        // eslint-disable-next-line no-console
                        console.error(e);
                    }
                })}
            {clonesHreflangs?.length > 0 &&
                clonesHreflangs
                    .filter((href) => !hreflangs?.includes(href.url))
                    .map((c, index) => {
                        return (
                            <link rel="alternate" hrefLang={c?.locale} href={c?.url} key={`cloneHreflang-${index}`} />
                        );
                    })}
            {availableLocale?.locale.map((langCode) => {
                if (langCode !== useLocale()) {
                    const currentUrl = availableLocale.url
                        ? urlHelper.getFullUrl(availableLocale.url, langCode, false)
                        : "";
                    return <link rel="alternate" hrefLang={langCode} href={currentUrl} key={langCode} />;
                }
            })}
            {shouldShowNoIndexTag() && <meta name="robots" content="noindex" />}
            <title>{title}</title>
            {((hasPaywall && level !== AccessLevel.level1) || hasPaywallL1) && (
                <script type="application/ld+json">{JSON.stringify(paywalledldJson).replace("&quot;", `"`)}</script>
            )}
            {!shouldShowNoIndexTag() && (
                <script type="application/ld+json">{JSON.stringify(publicldJson).replace("&quot;", `"`)}</script>
            )}{" "}
            {faqs?.length && (
                <script type="application/ld+json">
                    {`{
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        "specialty": "${pageTags.specialty || ""}",
                        "mainEntity": ${JSON.stringify(faqs)}
                    }`}
                </script>
            )}
            <meta property="og:title" content={open_graph?.title || title} />
            <meta property="og:description" content={open_graph?.description || description} />
            <meta property="og:type" content="website" />
            <meta property="og:url" content={url} />
            {open_graph?.image?.url && <meta property="og:image" content={open_graph?.image?.url} />}
            <meta name="twitter:title" content={twitter?.title || title} />
            <meta name="twitter:description" content={twitter?.description || description} />
            <meta name="twitter:card" content="summary_large_image" />
            {twitter?.image?.url && <meta name="twitter:image" content={twitter?.image?.url} />}
            <meta name="now4real-site-verification" content="vgxbl5PqbezQ1UwO3YZI" />
        </Helmet>
    );
};
